import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, CircularProgress, Alert, Avatar, TextField, Button } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getVendorDashboard } from '../services/Api';

import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const StatCard = ({ title, value, icon, color, gradient }) => (
  <Card sx={{
    borderRadius: 4,
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 12px 28px 0 rgba(0,0,0,0.12)',
    },
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    border: '1px solid rgba(0,0,0,0.03)'
  }}>
    <Box sx={{
      position: 'absolute',
      top: -30,
      right: -30,
      width: 120,
      height: 120,
      borderRadius: '50%',
      background: gradient,
      opacity: 0.15,
      zIndex: 0
    }} />

    <CardContent sx={{ position: 'relative', zIndex: 1, p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{
          background: `linear-gradient(135deg, ${color}20, ${color}40)`,
          color: color,
          width: 56,
          height: 56,
          borderRadius: 3,
          boxShadow: `0 4px 10px 0 ${color}30`
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await getVendorDashboard(fromDate, toDate);
      if (response.success) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'An error occurred while fetching dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleFilter = () => {
    fetchDashboardData();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const summary = data?.summary || {};
  const bookings = summary.bookings || {};

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: '800', color: '#1a237e', mb: 1, letterSpacing: '-0.5px' }}>
            Vendor Dashboard
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Welcome back! Here's what's happening with your business today.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>From Date</Typography>
            <TextField
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              size="small"
            />
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>To Date</Typography>
            <TextField
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              size="small"
            />
          </Box>
          <Button variant="contained" onClick={handleFilter} sx={{ height: '40px' }}>
            Filter
          </Button>
        </Box>
      </Box>

      {/* Top Row: Total Revenue & Total Bookings */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Total Revenue"
            value={`₹${summary.revenue?.total || 0}`}
            icon={<CurrencyRupeeIcon fontSize="large" />}
            color="#1976d2"
            gradient="linear-gradient(135deg, #1976d2, #64b5f6)"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Total Bookings"
            value={bookings.total || 0}
            icon={<ListAltIcon fontSize="large" />}
            color="#2e7d32"
            gradient="linear-gradient(135deg, #2e7d32, #81c784)"
          />
        </Grid>


        {/* Bottom Row: Preparing, Out for Delivery, Delivered */}

        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Preparing"
            value={bookings.preparing || 0}
            icon={<AutorenewIcon fontSize="medium" />}
            color="#ed6c02"
            gradient="linear-gradient(135deg, #ed6c02, #ffb74d)"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Out for Delivery"
            value={bookings.outForDelivery || 0}
            icon={<LocalShippingIcon fontSize="medium" />}
            color="#9c27b0"
            gradient="linear-gradient(135deg, #9c27b0, #ba68c8)"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <StatCard
            title="Delivered"
            value={bookings.delivered || 0}
            icon={<CheckCircleIcon fontSize="medium" />}
            color="#4caf50"
            gradient="linear-gradient(135deg, #4caf50, #81c784)"
          />
        </Grid>
      </Grid>


      {/* Chart Section */}
      <Card sx={{
        borderRadius: 4,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.03)',
        p: 3
      }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: '700', color: '#2c3e50' }}>
              Revenue & Bookings Overview
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Monthly performance analytics for the current year.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data?.monthlyAnalytics || []}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888', fontSize: 12 }}
                dy={10}
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888', fontSize: 12 }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)' }}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />

              <Area
                yAxisId="left"
                type="monotone"
                dataKey="revenue"
                stroke="#1976d2"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="Revenue (₹)"
                activeDot={{ r: 6, strokeWidth: 0, fill: '#1976d2' }}
              />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="bookings"
                stroke="#2e7d32"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorBookings)"
                name="Bookings"
                activeDot={{ r: 6, strokeWidth: 0, fill: '#2e7d32' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Card>
    </Box>
  );
};

export default Dashboard;
